import React, { useState } from "react";
import { useNavigate } from "react-router-dom";
import { toast } from "sonner";
import { api } from "../lib/api";
import { Button } from "./ui/button";
import {
  Dialog, DialogContent, DialogHeader, DialogTitle, DialogFooter,
} from "./ui/dialog";
import {
  DropdownMenu, DropdownMenuContent, DropdownMenuItem, DropdownMenuTrigger,
  DropdownMenuSeparator,
} from "./ui/dropdown-menu";
import { MoreVertical, Archive, Trash2, RotateCcw } from "lucide-react";

/**
 * Owner-only actions menu (Archive / Unarchive / Delete) for a single event.
 * Shared by:
 *   - EventsList event cards (renders as a compact ⋮ icon; card is a <Link>,
 *     so every button here stops-propagation + prevents-default)
 *   - EventDetail page header (renders as a full-size button, no link parent)
 *
 * Props:
 *   ev          — the event dict (must have id, title, role, archived_at)
 *   onChanged   — invoked after archive/unarchive/delete succeeds
 *   variant     — "icon" (compact ⋮) | "button" (larger pill w/ label). Default "icon".
 *   afterDelete — where to navigate after a successful delete. If not given,
 *                 stays on the current page (EventsList reload handles it).
 */
export default function EventActionsMenu({
  ev,
  onChanged,
  variant = "icon",
  afterDelete,
}) {
  const navigate = useNavigate();
  const isOwner = (ev.role || "owner") === "owner";
  const isArchived = !!ev.archived_at;
  const [busy, setBusy] = useState(false);
  const [confirmDelete, setConfirmDelete] = useState(false);

  if (!isOwner) return null;

  // The events-list card wraps this in a <Link>; buttons need to stop the
  // link click. Detail-page host has no link, so this is a harmless no-op.
  const stopNav = (e) => { e?.preventDefault?.(); e?.stopPropagation?.(); };

  const archive = async (e) => {
    stopNav(e); setBusy(true);
    try {
      await api.post(`/events/${ev.id}/archive`);
      toast.success(`Archived “${ev.title}”. Find it under the Archived filter.`);
      onChanged?.();
    } catch { toast.error("Could not archive event"); }
    finally { setBusy(false); }
  };
  const unarchive = async (e) => {
    stopNav(e); setBusy(true);
    try {
      await api.post(`/events/${ev.id}/unarchive`);
      toast.success(`Restored “${ev.title}” to your active events.`);
      onChanged?.();
    } catch { toast.error("Could not unarchive event"); }
    finally { setBusy(false); }
  };
  const del = async (e) => {
    stopNav(e); setBusy(true);
    try {
      await api.delete(`/events/${ev.id}`);
      toast.success(`Deleted “${ev.title}” and all associated data.`);
      setConfirmDelete(false);
      if (afterDelete) navigate(afterDelete);
      else onChanged?.();
    } catch { toast.error("Could not delete event"); }
    finally { setBusy(false); }
  };

  const trigger = variant === "button" ? (
    <Button
      variant="outline"
      onClick={stopNav}
      data-testid={`event-actions-${ev.id}`}
      className="rounded-sm border-hair h-9 px-3 gap-1.5 font-mono text-[10px] tracking-[0.2em] uppercase"
    >
      <MoreVertical size={13} strokeWidth={2} />
      Actions
    </Button>
  ) : (
    <button
      onClick={stopNav}
      className="text-muted hover:text-app p-0.5 border border-transparent hover:border-hair"
      data-testid={`event-actions-${ev.id}`}
      aria-label="Event actions"
    >
      <MoreVertical size={14} strokeWidth={2} />
    </button>
  );

  return (
    <>
      <DropdownMenu>
        <DropdownMenuTrigger asChild>{trigger}</DropdownMenuTrigger>
        <DropdownMenuContent align="end" className="w-52 bevel border-hair" onClick={stopNav}>
          {isArchived ? (
            <DropdownMenuItem
              onSelect={unarchive}
              disabled={busy}
              className="text-app cursor-pointer"
              data-testid={`event-unarchive-${ev.id}`}
            >
              <RotateCcw size={13} strokeWidth={2} className="mr-2" /> Restore from archive
            </DropdownMenuItem>
          ) : (
            <DropdownMenuItem
              onSelect={archive}
              disabled={busy}
              className="text-app cursor-pointer"
              data-testid={`event-archive-${ev.id}`}
            >
              <Archive size={13} strokeWidth={2} className="mr-2" /> Archive event
            </DropdownMenuItem>
          )}
          <DropdownMenuSeparator />
          <DropdownMenuItem
            onSelect={(e) => { stopNav(e); setConfirmDelete(true); }}
            disabled={busy}
            className="text-[#DC2626] focus:text-[#DC2626] cursor-pointer"
            data-testid={`event-delete-${ev.id}`}
          >
            <Trash2 size={13} strokeWidth={2} className="mr-2" /> Delete permanently
          </DropdownMenuItem>
        </DropdownMenuContent>
      </DropdownMenu>

      <Dialog open={confirmDelete} onOpenChange={(v) => !v && setConfirmDelete(false)}>
        <DialogContent className="max-w-md bevel-lg" onClick={stopNav}>
          <DialogHeader>
            <DialogTitle className="font-serif text-2xl text-app flex items-center gap-2">
              <Trash2 size={18} className="text-[#DC2626]" /> Delete “{ev.title}”?
            </DialogTitle>
          </DialogHeader>
          <div className="text-sm text-muted leading-relaxed py-2">
            This permanently removes the event and{" "}
            <strong className="text-app">every associated record</strong>: guests, vendors, budget,
            tasks, timeline, documents, chat threads, floor plan. This action{" "}
            <strong className="text-app">cannot be undone</strong>.
          </div>
          <div className="text-xs text-muted border border-hair bg-inset rounded-sm p-3 leading-relaxed">
            If you might want this event back later, use <strong className="text-app">Archive</strong> instead —
            it hides the event without deleting any data.
          </div>
          <DialogFooter>
            <Button
              variant="outline"
              onClick={(e) => { stopNav(e); setConfirmDelete(false); }}
              data-testid="event-delete-cancel"
            >
              Cancel
            </Button>
            <Button
              onClick={del}
              disabled={busy}
              data-testid="event-delete-confirm"
              className="rounded-sm bg-[#DC2626] hover:bg-[#B91C1C] text-white"
            >
              <Trash2 size={13} strokeWidth={2} className="mr-1.5" />
              {busy ? "Deleting…" : "Delete permanently"}
            </Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>
    </>
  );
}
